import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart } from 'lucide-react';
import { IMAGES } from '../mock';
import lotusIcon from '../assets/lotus-icon.png';

export default function Hero() {
  return (
    <section className="relative" style={{ background: 'linear-gradient(90deg, #FAF5EE 0%, #F5EEE4 42%, transparent 42%)' }}>
      <div className="relative grid lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.6fr)]">

        {/* LEFT PANEL — cream background with brand + hero content */}
        <div className="relative z-10 px-6 md:px-10 lg:px-14 xl:px-16 py-10 md:py-14 lg:py-16 reveal-left" style={{ background: 'linear-gradient(180deg, #FAF5EE 0%, #F5EEE4 100%)' }}>

          {/* Brand lockup */}
          <div className="flex items-start gap-4 mb-8 reveal">
            <img src={lotusIcon} alt="Lotus" className="w-14 h-14 md:w-16 md:h-16 object-contain animate-float flex-shrink-0" />
            <div>
              <div className="font-serif-display font-bold tracking-[0.14em] text-[color:var(--deep-sage)] text-lg md:text-xl lg:text-[1.55rem] leading-[1.1]">
                CHESAPEAKE<br />
                COUNSELING CENTER
              </div>
              <div className="mt-1 flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[color:var(--sage-dark)] font-semibold">
                <span className="w-6 h-px bg-[color:var(--sage-dark)]" />
                Let Your Light Shine
                <span className="w-6 h-px bg-[color:var(--sage-dark)]" />
              </div>
              <div className="font-script text-xl md:text-2xl text-[color:var(--taupe-gray)] mt-1">Grace. Gratitude. Growth.</div>
            </div>
          </div>

          {/* Headline */}
          <h1 className="font-serif-display font-bold text-[color:var(--deep-sage)] text-4xl md:text-5xl lg:text-[3.6rem] xl:text-[4.15rem] leading-[1.05] animate-fade-up reveal" style={{ animationDelay: '0.15s' }}>
            Healing Hearts.<br />
            Restoring Hope.
          </h1>
          <div className="font-script text-[color:var(--dusty-rose)] text-4xl md:text-5xl lg:text-[3.6rem] xl:text-[4.15rem] leading-[1.05] mt-2 animate-fade-up reveal text-shine" style={{ animationDelay: '0.25s' }}>
            Let Your Light Shine.
          </div>

          {/* Divider with heart */}
          <div className="flex items-center gap-3 my-6 md:my-7 max-w-sm reveal">
            <div className="flex-1 h-px bg-[color:var(--soft-border)]" />
            <Heart className="w-4 h-4 text-[color:var(--dusty-rose)]" strokeWidth={1.6} fill="rgba(232,165,169,0.25)" />
            <div className="flex-1 h-px bg-[color:var(--soft-border)]" />
          </div>

          {/* Body copy */}
          <p className="text-[color:var(--deep-sage)] text-base md:text-lg leading-relaxed max-w-md font-medium reveal">
            Compassionate counseling for individuals, couples, and families. We help you heal, grow, and reconnect — so you can live with purpose and peace.
          </p>

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap gap-3 reveal">
            <Link to="/schedule" className="btn-primary btn-glow uppercase tracking-[0.14em] text-xs md:text-sm" style={{ padding: '0.9rem 1.5rem' }}>
              Schedule an Appointment
            </Link>
            <Link
              to="/philosophy"
              className="btn-secondary uppercase tracking-[0.14em] text-xs md:text-sm"
              style={{ padding: '0.9rem 1.5rem' }}
              aria-label="Learn more about our counseling philosophy"
            >
              Our Philosophy
            </Link>
          </div>
        </div>

        {/* RIGHT — lotus lake image + overlay card */}
        <div className="relative min-h-[420px] md:min-h-[560px] lg:min-h-[640px] overflow-visible">
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={IMAGES.heroLotusSunrise}
              alt="Sunrise over a calm lake with a pink lotus flower"
              className="w-full h-full object-cover animate-slow-zoom"
            />

            {/* Soft cream feather on left edge blending into panel */}
            <div className="absolute inset-y-0 left-0 w-32 md:w-48 pointer-events-none" style={{ background: 'linear-gradient(90deg, rgba(245,238,228,0.95) 0%, rgba(245,238,228,0) 100%)' }} />
            {/* Right white feather that softens edge of overlay card */}
            <div className="absolute inset-y-0 right-0 w-24 md:w-40 pointer-events-none opacity-70" style={{ background: 'linear-gradient(270deg, rgba(255,252,247,0.55) 0%, rgba(255,252,247,0) 100%)' }} />
          </div>

          {/* Floating "Our Foundation" card — upper-left corner of the hero image */}
          <div className="hidden md:block absolute left-4 lg:left-8 xl:left-12 top-6 lg:top-10 w-[260px] lg:w-[300px] xl:w-[320px] reveal-scale z-20">
            <div className="rounded-xl p-6 lg:p-7 text-center relative overflow-hidden" style={{ background: 'rgba(255, 252, 247, 0.92)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.9)', boxShadow: '0 30px 60px -25px rgba(78,87,69,0.35)' }}>
              {/* subtle botanical corners */}
              <div className="absolute -bottom-3 -left-3 w-16 h-16 rounded-full opacity-30" style={{ background: 'radial-gradient(circle, rgba(184,190,164,0.6), transparent 70%)' }} />
              <div className="absolute -top-3 -right-3 w-14 h-14 rounded-full opacity-30" style={{ background: 'radial-gradient(circle, rgba(232,165,169,0.6), transparent 70%)' }} />

              <div className="font-script text-2xl text-[color:var(--dusty-rose)] mb-2">Our Foundation</div>
              <div className="font-serif-display font-bold text-[color:var(--deep-sage)] tracking-[0.14em] text-base lg:text-lg">GRACE &amp; GRATITUDE</div>
              <div className="mx-auto my-3 w-10 h-px bg-[color:var(--soft-border)]" />
              <Heart className="w-3.5 h-3.5 mx-auto text-[color:var(--dusty-rose)]" fill="rgba(232,165,169,0.4)" strokeWidth={1.5} />
              <div className="mx-auto my-3 w-10 h-px bg-[color:var(--soft-border)]" />

              <p className="text-[13px] lg:text-sm text-[color:var(--deep-sage)] leading-relaxed font-medium">
                We believe in the power of grace to heal, gratitude to transform, and connection to inspire.
              </p>

              <div className="my-4 flex items-center gap-2">
                <div className="flex-1 h-px bg-[color:var(--soft-border)]" />
                <div className="flex-1 h-px bg-[color:var(--soft-border)]" />
              </div>

              <div className="font-serif-display font-bold text-[color:var(--deep-sage)] tracking-[0.14em] text-xs lg:text-sm">WE HELP YOU CHOOSE</div>
              <div className="mt-2 font-serif-display font-bold text-[color:var(--dusty-rose)] tracking-[0.1em] text-sm lg:text-[15px] leading-[1.5]">
                LOVE. &nbsp; COURAGE.<br />
                SOUND MIND.
              </div>

              <p className="mt-4 text-[12px] lg:text-[13px] text-[color:var(--taupe-gray)] italic leading-relaxed">
                Not living from a place of fear, but from a place of hope.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
